import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { ArrowRight, BookOpen, Video, Award } from 'lucide-react';

const GlenOrbikCoursePage = () => {
    return (
        <div data-testid="glen-orbik-course-page">
            <SEO
                title="Glen Orbik Drawing Course — The Only Recorded Class"
                description="Study drawing and painting with Glen Orbik in what is believed to be the only comprehensive recording of his instruction. 10-week course in the Frank Reilly tradition at Zarolla."
                url="/glen-orbik-drawing-course"
                keywords={['Glen Orbik', 'Glen Orbik drawing course', 'Glen Orbik art class', 'Reilly method drawing', 'classical drawing instruction']}
                structuredData={{
                    '@context': 'https://schema.org',
                    '@type': 'Course',
                    '@id': 'https://zarolla.com/glen-orbik-drawing-course#course',
                    url: 'https://zarolla.com/glen-orbik-drawing-course',
                    name: 'Glen Orbik Drawing Course — The Only Recorded Class',
                    description: 'The only known comprehensive recording of Glen Orbik teaching drawing in the Frank Reilly tradition. A 10-week course covering head construction, figure drawing, and classical illustration technique.',
                    provider: {
                        '@type': 'Organization',
                        name: 'Zarolla Online Art Academy',
                        url: 'https://zarolla.com',
                    },
                    instructor: {
                        '@type': 'Person',
                        name: 'Glen Orbik',
                        jobTitle: 'Illustrator and Art Instructor',
                    },
                    hasCourseInstance: {
                        '@type': 'CourseInstance',
                        courseMode: 'online',
                        courseWorkload: 'PT20H',
                    },
                    isPartOf: { '@id': 'https://zarolla.com/#website' },
                    datePublished: '2007-01-01',
                    dateModified: new Date().toISOString().split('T')[0],
                    inLanguage: 'en-US',
                }}
            />

            <div className="section-gap">
                <div className="container-main">
                    {/* Hero */}
                    <div className="max-w-3xl mb-16">
                        <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                            One-of-a-kind archive
                        </span>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-primary leading-tight mt-4">
                            Glen Orbik Drawing Course
                        </h1>
                        <p className="text-muted-foreground text-lg mt-6 max-w-2xl">
                            What is believed to be the only comprehensive recording of Glen Orbik teaching. A complete 10-week course in classical drawing and illustration, grounded in the Frank Reilly tradition he inherited from Fred Fixler.
                        </p>
                        <div className="flex flex-wrap gap-4 mt-8">
                            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 text-base px-8 py-6" data-testid="orbik-view-course-btn">
                                <Link to="/courses/9">
                                    View Course Details <ArrowRight className="ml-2 h-4 w-4" />
                                </Link>
                            </Button>
                            <Button asChild variant="outline" size="lg" className="text-base px-8 py-6" data-testid="orbik-about-btn">
                                <Link to="/instructor/glen-orbik">
                                    About Glen Orbik
                                </Link>
                            </Button>
                        </div>
                    </div>

                    {/* Section 1: Why This Course Matters */}
                    <section className="py-12 border-t border-border" data-testid="orbik-section-1">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                            <div>
                                <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                                    Why this recording matters
                                </h2>
                                <p className="text-muted-foreground leading-relaxed mb-4">
                                    Glen Orbik (1963&ndash;2015) was one of the most accomplished illustrators and drawing instructors of his generation. Trained by Fred Fixler &mdash; a direct student of Frank Reilly at the Art Students League of New York &mdash; Orbik carried forward an unbroken lineage of classical draftsmanship stretching back to the golden age of American illustration.
                                </p>
                                <p className="text-muted-foreground leading-relaxed mb-4">
                                    After Orbik's passing, his teaching became irreplaceable. Zarolla's archive preserves what is believed to be the only complete recording of his instructional method: the explanations, the demonstrations, the reasoning behind each decision. For students today, these recordings are a direct connection to one of the last great practitioners of the Reilly tradition.
                                </p>
                                <p className="text-muted-foreground leading-relaxed">
                                    His students &mdash; including <Link to="/instructor/tony-pro" className="text-accent hover:underline">Tony Pro</Link> and <Link to="/instructor/jeremy-lipking" className="text-accent hover:underline">Jeremy Lipking</Link> &mdash; have gone on to become some of today's most celebrated figurative painters, a testament to the effectiveness of his teaching.
                                </p>
                            </div>
                            <div className="bg-secondary/30 rounded-sm p-8">
                                <h3 className="font-medium text-primary mb-4">Course highlights:</h3>
                                <ul className="space-y-3 text-muted-foreground">
                                    <li className="flex items-start gap-3">
                                        <BookOpen className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                                        <span>10-week structured curriculum covering head construction, figure drawing, and value</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Video className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                                        <span>Full-length class sessions with live demonstration and explanation</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Award className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                                        <span>Reilly method instruction from one of its most accomplished modern practitioners</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1 flex-shrink-0">&mdash;</span>
                                        <span>Lifetime access to all lessons &mdash; study at your own pace</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* Section 2: What You'll Learn */}
                    <section className="py-12 border-t border-border" data-testid="orbik-section-2">
                        <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                            What you'll learn
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div>
                                <h3 className="font-medium text-primary mb-3">Classical head construction</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    Orbik's version of the Reilly Abstract &mdash; a systematic method for constructing the human head from any angle. Learn the proportional framework that separates professional-level drawing from guesswork.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-medium text-primary mb-3">Value structure and light logic</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    How to separate light from shadow, control halftones, and build convincing form through value alone. Orbik inherited Fixler's emphasis on value discipline, and it shows in every demonstration.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-medium text-primary mb-3">Professional illustration technique</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    Beyond academic exercises, Orbik demonstrates how classical construction powers professional work &mdash; from book covers and comics to gallery paintings. Construction in service of picture-making.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Section 3: The Reilly Lineage */}
                    <section className="py-12 border-t border-border" data-testid="orbik-section-3">
                        <div className="max-w-3xl">
                            <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                                The Reilly tradition: from the Art Students League to your screen
                            </h2>
                            <p className="text-muted-foreground leading-relaxed mb-4">
                                Frank Reilly developed his systematic drawing method at the Art Students League of New York, where he taught from the 1930s until 1967. His students included some of the most accomplished illustrators in American history. Among them was <strong>Fred Fixler</strong>, a retired movie-poster illustrator who passed the method to a new generation.
                            </p>
                            <p className="text-muted-foreground leading-relaxed mb-4">
                                Glen Orbik studied under Fixler and became one of the most faithful yet creatively dynamic carriers of the Reilly approach. His client list included Random House, DC Comics, Bantam Books, and film studios. His teaching combined Fixler's structural rigor with his own instinct for dramatic narrative and pictorial impact.
                            </p>
                            <p className="text-muted-foreground leading-relaxed mb-6">
                                This course represents a rare opportunity: direct access to instruction in the Reilly tradition from one of its most accomplished modern interpreters &mdash; preserved for students who can no longer study with Orbik in person.
                            </p>
                            <div className="flex flex-wrap gap-3">
                                <Button asChild variant="outline" size="sm">
                                    <Link to="/methods/frank-reilly-drawing-method">
                                        Read: The Frank Reilly Method
                                    </Link>
                                </Button>
                                <Button asChild variant="outline" size="sm">
                                    <Link to="/instructor/glen-orbik">
                                        Full Orbik Biography
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </section>

                    {/* Section 4: CTA */}
                    <section className="py-12 border-t border-border" data-testid="orbik-section-cta">
                        <div className="max-w-2xl">
                            <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                                Begin studying with Glen Orbik
                            </h2>
                            <p className="text-muted-foreground leading-relaxed mb-8">
                                View the full course curriculum, pricing tiers, and lesson previews. Choose the package that fits your study goals and start learning from one of the most important drawing instructors of the modern era.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90">
                                    <Link to="/courses/9">
                                        View Course & Pricing <ArrowRight className="ml-2 h-4 w-4" />
                                    </Link>
                                </Button>
                                <Button asChild variant="outline" size="lg">
                                    <Link to="/courses">Browse All Courses</Link>
                                </Button>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default GlenOrbikCoursePage;
